import {
  ManifestItem,
  EnrichmentStatus,
  EnrichmentItem,
  EnrichmentStatusDictionary,
} from "./types";
import { parseCfi, CFI } from "epubjs";
import { createSelector } from "@reduxjs/toolkit";
import { getCurrentPageCfi, isPageLoading } from "../nav/load-status/selectors";
import { DetailedLoadStatus } from "../../../../../../common/utils/async-status";
import { safeParseCfi } from "../../../../../../common/utils/cfi";

function getBase(state) {
  return state.enrichments;
}

export function getAllEnrichments(state): ManifestItem[] {
  return getBase(state).enrichments;
}

export function getManifestLoadStatus(state): DetailedLoadStatus {
  return getBase(state).manifestLoadStatus.status;
}

function getManifestLoadError(state): number | undefined {
  const status = getManifestLoadStatus(state);
  if (status === DetailedLoadStatus.ERROR) {
    return getBase(state).manifestLoadStatus.errorCode;
  }
}

export function isManifestBlockedByPublisher(state): boolean {
  return getManifestLoadError(state) === 403;
}

export function getServicesLoadStatus(state): DetailedLoadStatus {
  return getBase(state).servicesLoadStatus;
}

export function getEnrichmentStatusLoadStatus(state): DetailedLoadStatus {
  return getBase(state).enrichmentStatusLoadStatus;
}

export function getEnrichmentStatus(state): EnrichmentStatusDictionary {
  return getBase(state).enrichmentStatus;
}

export const getEnrichmentIDsForCurrentDocument = createSelector<
  any,
  ManifestItem[],
  string,
  boolean,
  string[]
>(
  (state) => getAllEnrichments(state),
  (state) => getCurrentPageCfi(state),
  (state) => isPageLoading(state),
  (allEnrichments, currentCfi, isLoading) => {
    if (!currentCfi || isLoading) {
      return [];
    }
    return filterEnrichmentsToDoc(allEnrichments, currentCfi).map(
      (manifestItem) => manifestItem.id
    );
  }
);

export const getEnrichmentsForCurrentDocument = createSelector<
  any,
  ManifestItem[],
  string,
  boolean,
  EnrichmentStatusDictionary,
  EnrichmentItem[]
>(
  (state) => getAllEnrichments(state),
  (state) => getCurrentPageCfi(state),
  (state) => isPageLoading(state),
  (state) => getEnrichmentStatus(state),
  (allEnrichments, currentCfi, isLoading, enrichmentStatus) => {
    if (!currentCfi || isLoading) {
      return [];
    }
    return filterEnrichmentsToDoc(allEnrichments, currentCfi).map(
      (manifestItem) => {
        return {
          id: manifestItem.id,
          cfi: manifestItem.cfi,
          count: manifestItem.metadata.contents_count,
          type: manifestItem.metadata.assessment_type,
          status:
            enrichmentStatus?.[manifestItem.id] ?? EnrichmentStatus.INCOMPLETE,
        };
      }
    );
  }
);

const filterEnrichmentsToDoc = (
  allEnrichments: ManifestItem[],
  currentCfi: string
): ManifestItem[] => {
  const pageCfi = parseCfi(currentCfi);
  return allEnrichments.filter((enrichment) => {
    const cfiToUse = safeParseCfi(enrichment.cfi)?.contentDoc;
    if (cfiToUse) {
      return CFI.equals(pageCfi, cfiToUse, { checkAssertions: true });
    }
    return false;
  });
};
